(function (window, document) {
  'use strict';

  var VERSION = '19.4.7-r3';
  var existing = window.GLLFProductSectionLifecycle;
  if (existing && existing.version === VERSION) {
    if (typeof existing.scan === 'function') existing.scan(document);
    return;
  }

  var owners = new Map();
  var roots = new WeakMap();
  var bypassedSubmissions = new WeakSet();
  var ownerSerial = 0;

  function text(value) {
    return value == null ? '' : String(value);
  }

  function ownerKey(sectionId, productId) {
    return text(sectionId) + '::' + text(productId);
  }

  function stable(value, seen) {
    if (value == null || typeof value !== 'object') return JSON.stringify(value);
    seen = seen || [];
    if (seen.indexOf(value) >= 0) return '"[Circular]"';
    seen.push(value);
    var out;
    if (Array.isArray(value)) {
      out = '[' + value.map(function (item) { return stable(item, seen); }).join(',') + ']';
    } else {
      out = '{' + Object.keys(value).sort().map(function (key) {
        return JSON.stringify(key) + ':' + stable(value[key], seen);
      }).join(',') + '}';
    }
    seen.pop();
    return out;
  }

  function deepFreeze(value, seen) {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    seen = seen || [];
    if (seen.indexOf(value) >= 0) return value;
    seen.push(value);
    Object.keys(value).forEach(function (key) { deepFreeze(value[key], seen); });
    return Object.freeze(value);
  }

  function cloneSerializable(value) {
    if (value == null) return value;
    try { return JSON.parse(JSON.stringify(value)); } catch (error) { return null; }
  }

  function snapshotValueSignature(value) {
    var comparable = Object.assign({}, value || {});
    delete comparable.revision;
    delete comparable.reason;
    return stable(comparable);
  }

  function normalizeResult(result, fallbackCode) {
    if (result === undefined || result === null || result === true) {
      return { ok: true, code: 'OK', message: '', field: null, retryable: false };
    }
    if (result === false) {
      return { ok: false, code: fallbackCode || 'VALIDATION_REJECTED', message: 'Review the highlighted choice before continuing.', field: null, retryable: true };
    }
    if (typeof result === 'string') {
      return { ok: false, code: fallbackCode || 'VALIDATION_REJECTED', message: result, field: null, retryable: true };
    }
    return {
      ok: result.ok !== false,
      code: text(result.code || (result.ok === false ? fallbackCode || 'VALIDATION_REJECTED' : 'OK')),
      message: text(result.message || ''),
      field: result.field || null,
      retryable: result.retryable !== false,
      handled: result.handled === true,
      detail: result.detail || null
    };
  }

  function sectionRoot(input) {
    var root = input && input.root;
    if (root && root.nodeType === 1) {
      if (root.matches && root.matches('product-info[data-section]')) return root;
      var productInfo = root.closest && root.closest('product-info[data-section]');
      if (productInfo) return productInfo;
      productInfo = root.querySelector && root.querySelector('product-info[data-section]');
      if (productInfo) return productInfo;
    }
    var sectionId = text(input && input.sectionId);
    if (!sectionId) return null;
    return document.getElementById('MainProduct-' + sectionId) || document.querySelector('product-info[data-section="' + sectionId.replace(/["\\]/g, '\\$&') + '"]');
  }

  function createOwner(input) {
    var root = sectionRoot(input);
    if (!root) return null;
    var sectionId = text(input.sectionId || root.dataset.section);
    var productId = text(input.productId || root.dataset.productId);
    if (!sectionId || !productId) return null;

    var identityKey = ownerKey(sectionId, productId);
    var key = identityKey;
    while (owners.has(key) && owners.get(key).root !== root) key = identityKey + '::instance-' + (++ownerSerial);
    var participantMap = new Map();
    var disposers = [];
    var acceleratedReasons = new Set();
    var acceleratedOriginals = new Map();
    var destroyed = false;
    var currentForm = null;
    var config = {
      formId: text(input.formId || ('product-form-' + sectionId)),
      mode: text(input.mode || 'governed'),
      governed: input.governed !== false
    };
    var snapshot = deepFreeze({
      contract: 'gllf.product-section-lifecycle.v1',
      version: VERSION,
      key: key,
      identityKey: identityKey,
      sectionId: sectionId,
      productId: productId,
      formId: config.formId,
      mode: config.mode,
      governed: config.governed,
      revision: 0,
      status: 'mounted',
      reason: 'mount',
      participants: {},
      protection: null
    });
    var snapshotSignature = snapshotValueSignature(snapshot);

    function emit(name, detail) {
      if (!root || !root.dispatchEvent || typeof window.CustomEvent !== 'function') return;
      root.dispatchEvent(new window.CustomEvent(name, { bubbles: true, detail: detail }));
    }

    function updateSnapshot(patch, reason) {
      if (destroyed) return snapshot;
      var nextPatch = typeof patch === 'function' ? patch(snapshot) : patch;
      nextPatch = nextPatch && typeof nextPatch === 'object' ? nextPatch : {};
      var next = Object.assign({}, snapshot, cloneSerializable(nextPatch) || nextPatch, {
        revision: snapshot.revision + 1,
        reason: text(reason || nextPatch.reason || 'update')
      });
      var signature = snapshotValueSignature(next);
      if (signature === snapshotSignature) return snapshot;
      snapshot = deepFreeze(next);
      snapshotSignature = signature;
      emit('gllf:lifecycle-state', snapshot);
      return snapshot;
    }

    function configure(next) {
      next = next || {};
      if (next.formId) config.formId = text(next.formId);
      if (next.mode) config.mode = text(next.mode);
      if (typeof next.governed === 'boolean') config.governed = next.governed;
      updateSnapshot({ formId: config.formId, mode: config.mode, governed: config.governed }, 'configure');
      return owner;
    }

    function getForm() {
      var form = config.formId ? document.getElementById(config.formId) : null;
      if (form && root.contains(form)) return form;
      form = root.querySelector('form[action*="/cart/add"][data-type="add-to-cart-form"],form[action*="/cart/add"]');
      return form || null;
    }

    function context(form, event, phase) {
      return {
        owner: owner,
        root: root,
        sectionId: sectionId,
        productId: productId,
        formId: config.formId,
        form: form || getForm(),
        event: event || null,
        phase: phase || 'submit',
        snapshot: snapshot,
        revision: snapshot.revision
      };
    }

    function participantState(reason) {
      var state = {};
      participantMap.forEach(function (participant, name) {
        if (!participant || typeof participant.getSnapshot !== 'function') return;
        try { state[name] = cloneSerializable(participant.getSnapshot()) || null; } catch (error) { state[name] = { error: 'SNAPSHOT_UNAVAILABLE' }; }
      });
      updateSnapshot({ participants: state }, reason || 'participant-state');
    }

    function notifyForm(reason) {
      if (destroyed) return null;
      currentForm = getForm();
      var ctx = context(currentForm, null, 'forms-changed');
      participantMap.forEach(function (participant) {
        if (!participant) return;
        try {
          if (typeof participant.onFormsChanged === 'function') participant.onFormsChanged(ctx);
          else if (typeof participant.onForm === 'function') participant.onForm(ctx);
        } catch (error) {
          emit('gllf:lifecycle-error', { code: 'PARTICIPANT_FORM_SYNC_FAILED', message: text(error && error.message), sectionId: sectionId, productId: productId });
        }
      });
      applyAcceleratedState();
      emit('gllf:lifecycle:forms-changed', { sectionId: sectionId, productId: productId, formId: config.formId, form: currentForm, reason: reason || 'refresh' });
      return currentForm;
    }

    function registerParticipant(name, participant) {
      name = text(name);
      if (!name || !participant || destroyed) return function () {};
      var prior = participantMap.get(name);
      if (prior === participant) return function () { unregisterParticipant(name, participant); };
      if (prior && typeof prior.destroy === 'function') {
        try { prior.destroy({ reason: 'replaced', owner: owner }); } catch (error) {}
      }
      participantMap.set(name, participant);
      if (typeof participant.mount === 'function') participant.mount(context(getForm(), null, 'mount'));
      participantState('participant:' + name);
      notifyForm('participant:' + name);
      return function () { unregisterParticipant(name, participant); };
    }

    function unregisterParticipant(name, participant) {
      var current = participantMap.get(name);
      if (!current || (participant && current !== participant)) return false;
      participantMap.delete(name);
      if (typeof current.destroy === 'function') {
        try { current.destroy({ reason: 'unregister', owner: owner }); } catch (error) {}
      }
      participantState('participant-removed:' + name);
      return true;
    }

    function validationEntries(ctx) {
      var values = [];
      participantMap.forEach(function (participant, name) {
        if (!participant) return;
        try {
          if (typeof participant.beforeSubmit === 'function' && ctx.phase === 'submit') participant.beforeSubmit(ctx);
          if (typeof participant.validate === 'function') values.push({ name: name, value: participant.validate(ctx), participant: participant });
        } catch (error) {
          values.push({ name: name, value: { ok: false, code: 'PARTICIPANT_EXCEPTION', message: text(error && error.message) || 'This choice could not be validated.', retryable: true }, participant: participant });
        }
      });
      return values;
    }

    function finalizeValidation(entries, ctx) {
      return Promise.all(entries.map(function (entry) {
        return Promise.resolve(entry.value).then(function (value) {
          var result = normalizeResult(value, entry.name.toUpperCase().replace(/[^A-Z0-9]+/g, '_') + '_REJECTED');
          result.participant = entry.name;
          return result;
        }, function (error) {
          return { ok: false, code: 'PARTICIPANT_EXCEPTION', message: text(error && error.message) || 'This choice could not be validated.', field: null, retryable: true, participant: entry.name };
        });
      })).then(function (results) {
        var failure = results.find(function (result) { return !result.ok; });
        var handled = results.find(function (result) { return result.handled; });
        var finalResult = failure || handled || { ok: true, code: 'OK', message: '', field: null, retryable: false };
        finalResult = Object.assign({}, finalResult, { startedAtRevision: ctx.revision, stateRevision: snapshot.revision, results: results });
        if (failure) {
          var failed = participantMap.get(failure.participant);
          if (failed && typeof failed.onInvalid === 'function') {
            try { failed.onInvalid(failure, ctx); } catch (error) {}
          }
          emit('gllf:lifecycle-validation', finalResult);
        }
        return finalResult;
      });
    }

    function validateParticipants(options) {
      options = options || {};
      var ctx = context(options.form, options.event, options.phase || 'review');
      return finalizeValidation(validationEntries(ctx), ctx);
    }

    function onSubmit(event) {
      var form = event.target;
      if (!config.governed || !form || form !== getForm()) return;
      if (bypassedSubmissions.has(form)) {
        bypassedSubmissions.delete(form);
        return;
      }
      var ctx = context(form, event, 'submit');
      var entries = validationEntries(ctx);
      if (!entries.length) return;
      var async = entries.some(function (entry) { return entry.value && typeof entry.value.then === 'function'; });
      if (!async) {
        var results = entries.map(function (entry) {
          var value = normalizeResult(entry.value, entry.name.toUpperCase().replace(/[^A-Z0-9]+/g, '_') + '_REJECTED');
          value.participant = entry.name;
          return value;
        });
        var failure = results.find(function (result) { return !result.ok; });
        var handled = results.find(function (result) { return result.handled; });
        if (!failure && !handled) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        var chosen = failure || handled;
        chosen.startedAtRevision = ctx.revision;
        chosen.stateRevision = snapshot.revision;
        chosen.results = results;
        if (failure) {
          var participant = participantMap.get(failure.participant);
          if (participant && typeof participant.onInvalid === 'function') participant.onInvalid(failure, ctx);
          emit('gllf:lifecycle-validation', chosen);
        }
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      finalizeValidation(entries, ctx).then(function (result) {
        if (!result.ok || result.handled || destroyed || !root.contains(form)) return;
        bypassedSubmissions.add(form);
        if (typeof form.requestSubmit === 'function') form.requestSubmit(event.submitter || undefined);
        else form.submit();
      });
    }

    function acceleratedTargets() {
      return Array.from(root.querySelectorAll('.shopify-payment-button,[data-shopify="payment-button"],.additional-checkout-buttons'));
    }

    function rememberAccelerated(target) {
      if (acceleratedOriginals.has(target)) return;
      acceleratedOriginals.set(target, {
        hidden: !!target.hidden,
        ariaHiddenPresent: target.hasAttribute('aria-hidden'),
        ariaHidden: target.getAttribute('aria-hidden'),
        inertPresent: target.hasAttribute('inert'),
        inert: !!target.inert,
        tabIndexPresent: target.hasAttribute('tabindex'),
        tabIndex: target.getAttribute('tabindex')
      });
    }

    function restoreAccelerated(target, original) {
      if (!target || !original) return;
      target.hidden = original.hidden;
      if (original.ariaHiddenPresent) target.setAttribute('aria-hidden', original.ariaHidden);
      else target.removeAttribute('aria-hidden');
      if (original.inertPresent) target.setAttribute('inert', '');
      else target.removeAttribute('inert');
      if ('inert' in target) target.inert = original.inert;
      if (original.tabIndexPresent) target.setAttribute('tabindex', original.tabIndex);
      else target.removeAttribute('tabindex');
      target.removeAttribute('data-gllf-lifecycle-accelerated');
    }

    function applyAcceleratedState() {
      /* Governed plant forms never regain accelerated checkout during planner state changes. */
      var blocked = config.governed === true || acceleratedReasons.size > 0;
      acceleratedTargets().forEach(function (target) {
        if (blocked) {
          rememberAccelerated(target);
          target.hidden = true;
          target.setAttribute('aria-hidden', 'true');
          target.setAttribute('inert', '');
          if ('inert' in target) target.inert = true;
          target.setAttribute('tabindex', '-1');
          target.setAttribute('data-gllf-lifecycle-accelerated', 'blocked');
        } else if (acceleratedOriginals.has(target)) {
          restoreAccelerated(target, acceleratedOriginals.get(target));
          acceleratedOriginals.delete(target);
        }
      });
    }

    function setAcceleratedBlocked(reason, blocked) {
      reason = text(reason || 'governed');
      if (blocked) acceleratedReasons.add(reason);
      else acceleratedReasons.delete(reason);
      applyAcceleratedState();
      updateSnapshot({ acceleratedBlocked: config.governed === true || acceleratedReasons.size > 0, acceleratedReasons: Array.from(acceleratedReasons).sort() }, 'accelerated');
    }

    function addDisposer(disposer) {
      if (typeof disposer === 'function') disposers.push(disposer);
      return disposer;
    }

    function listen(target, type, listener, options) {
      if (!target || !target.addEventListener) return function () {};
      target.addEventListener(type, listener, options);
      return addDisposer(function () { target.removeEventListener(type, listener, options); });
    }

    function onProtectionState(event) {
      var detail = event && event.detail;
      if (!detail) return;
      if (detail.sectionId && text(detail.sectionId) !== sectionId) return;
      if (detail.productId && text(detail.productId) !== productId) return;
      updateSnapshot({ protection: cloneSerializable(detail) }, 'protection-state');
    }

    function onRootChange(event) {
      if (!event || !event.target) return;
      var form = getForm();
      if (form && (form.contains(event.target) || event.target.getAttribute && event.target.getAttribute('form') === config.formId)) {
        participantMap.forEach(function (participant) {
          if (participant && typeof participant.onChange === 'function') participant.onChange(context(form, event, 'change'));
        });
      }
    }

    function destroy(reason) {
      if (destroyed) return;
      destroyed = true;
      root.removeEventListener('submit', onSubmit, true);
      root.removeEventListener('change', onRootChange);
      root.removeEventListener('gllf:protection-state', onProtectionState);
      disposers.splice(0).reverse().forEach(function (dispose) { try { dispose(); } catch (error) {} });
      Array.from(participantMap.entries()).reverse().forEach(function (entry) {
        var participant = entry[1];
        if (participant && typeof participant.destroy === 'function') {
          try { participant.destroy({ reason: reason || 'destroy', owner: owner }); } catch (error) {}
        }
      });
      participantMap.clear();
      acceleratedOriginals.forEach(function (original, target) { restoreAccelerated(target, original); });
      acceleratedOriginals.clear();
      acceleratedReasons.clear();
      owners.delete(key);
      roots.delete(root);
      root.removeAttribute('data-gllf-lifecycle-owned');
      emit('gllf:lifecycle-destroyed', { sectionId: sectionId, productId: productId, reason: reason || 'destroy' });
    }

    var owner = {
      key: key,
      identityKey: identityKey,
      root: root,
      sectionId: sectionId,
      productId: productId,
      get formId() { return config.formId; },
      get destroyed() { return destroyed; },
      configure: configure,
      getForm: getForm,
      getSnapshot: function () { return snapshot; },
      updateSnapshot: updateSnapshot,
      syncParticipants: participantState,
      registerParticipant: registerParticipant,
      getParticipant: function (name) { return participantMap.get(text(name)) || null; },
      unregisterParticipant: unregisterParticipant,
      validateParticipants: validateParticipants,
      refreshForms: notifyForm,
      setAcceleratedBlocked: setAcceleratedBlocked,
      addDisposer: addDisposer,
      listen: listen,
      destroy: destroy
    };

    owners.set(key, owner);
    roots.set(root, owner);
    root.addEventListener('submit', onSubmit, true);
    root.addEventListener('change', onRootChange);
    root.addEventListener('gllf:protection-state', onProtectionState);
    root.setAttribute('data-gllf-lifecycle-owned', key);
    emit('gllf:lifecycle-mounted', snapshot);
    return owner;
  }

  function getOrCreate(input) {
    input = input || {};
    var root = sectionRoot(input);
    if (root && roots.has(root)) {
      var rooted = roots.get(root);
      var nextSectionId = text(input.sectionId || root.dataset.section);
      var nextProductId = text(input.productId || root.dataset.productId);
      if (rooted.sectionId === nextSectionId && rooted.productId === nextProductId) return rooted.configure(input);
      rooted.destroy('identity-changed');
    }
    var sectionId = text(input.sectionId || (root && root.dataset.section));
    var productId = text(input.productId || (root && root.dataset.productId));
    var found = owners.get(ownerKey(sectionId, productId));
    if (found && (!root || found.root === root)) return found.configure(input);
    return createOwner(Object.assign({}, input, { root: root, sectionId: sectionId, productId: productId }));
  }

  function registerMarker(marker) {
    if (!marker || !marker.dataset) return null;
    var root = sectionRoot({ root: marker, sectionId: marker.dataset.sectionId });
    return getOrCreate({
      root: root,
      sectionId: marker.dataset.sectionId || (root && root.dataset.section),
      productId: marker.dataset.productId || (root && root.dataset.productId),
      formId: marker.dataset.formId,
      mode: marker.dataset.lifecycleMode,
      governed: marker.dataset.governed !== 'false' && marker.dataset.governed !== '0'
    });
  }

  function registerParticipant(input) {
    input = input || {};
    var owner = getOrCreate(input);
    return owner ? owner.registerParticipant(input.name, input.participant) : function () {};
  }

  function scan(scope) {
    scope = scope && scope.querySelectorAll ? scope : document;
    var markers = [];
    if (scope.matches && scope.matches('[data-gllf-product-lifecycle]')) markers.push(scope);
    markers = markers.concat(Array.from(scope.querySelectorAll('[data-gllf-product-lifecycle]')));
    markers.forEach(function (marker) {
      var owner = registerMarker(marker);
      if (owner) owner.refreshForms('scan');
    });
    return markers.length;
  }

  function onSectionLoad(event) {
    if (event && event.target) scan(event.target);
  }

  function onSectionUnload(event) {
    var target = event && event.target;
    if (!target) return;
    Array.from(owners.values()).forEach(function (owner) {
      if (target === owner.root || target.contains(owner.root) || owner.root.contains(target)) owner.destroy('shopify:section:unload');
    });
  }

  var api = {
    version: VERSION,
    contract: 'gllf.product-section-lifecycle.v1',
    getOrCreate: getOrCreate,
    get: function (sectionId, productId, root) {
      if (root && roots.has(root)) return roots.get(root);
      var identity = ownerKey(sectionId, productId), matches = Array.from(owners.values()).filter(function (owner) { return owner.identityKey === identity; });
      return matches.length === 1 ? matches[0] : null;
    },
    registerMarker: registerMarker,
    registerParticipant: registerParticipant,
    scan: scan,
    destroy: function (sectionId, productId, reason) {
      Array.from(owners.values()).filter(function (owner) { return owner.identityKey === ownerKey(sectionId, productId); }).forEach(function (owner) { owner.destroy(reason || 'api'); });
    },
    _test: { deepFreeze: deepFreeze, normalizeResult: normalizeResult, ownerKey: ownerKey, stable: stable }
  };

  window.GLLFProductSectionLifecycle = api;
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(document); }, { once: true });
  else scan(document);
})(window, document);
